import { logger } from './logger';
import {
    FmtpConfigItem,
    MediaConfigItem,
    MediaSection,
    RtcpFbConfigItem,
    RtpConfigItem,
    SessionDescription,
} from './types';

function patchMediaSectionPayloads(media: MediaSection, oldPayloadType: number, newPayloadType: number) {
    if (!media.payloads) {
        return;
    }

    media.payloads = media.payloads
        .split(' ')
        .map((payloadType) => (payloadType === String(oldPayloadType) ? String(newPayloadType) : payloadType))
        .join(' ');
}

type PayloadTypeOptionsElement = {
    payload: number;
    match: {
        rtp: (item: RtpConfigItem) => boolean;
        fmtp?: (item: FmtpConfigItem) => boolean;
        rtcpFb?: (item: RtcpFbConfigItem) => boolean;
    };
};

export type PayloadTypeOptions = {
    audio?: PayloadTypeOptionsElement[];
    video?: PayloadTypeOptionsElement[];
};

/**
 * Находит секции, подходящие под match и меняем им payload type. Так же меняем payload type в общем списке payloads.
 * Логику матча можно посмотреть в доке lodash/matches.
 * Используем такой подход, чтобы не нужно было указывать payload в каждом объекте.
 */
export function applyPayloadTypes(sdp: SessionDescription, payloadTypes: PayloadTypeOptions) {
    sdp.media.forEach((mediaSection) => {
        const mediaPayloadTypes = payloadTypes[mediaSection.type as 'audio' | 'video'];

        if (!mediaPayloadTypes) {
            return;
        }

        /**
         * Пытаемся применить каждую из прокинутых опций.
         */
        mediaPayloadTypes.forEach((payloadTypeOption) => {
            const newPayloadType = payloadTypeOption.payload;
            /**
             * Для каждого параметра из матчера (rtp, fmtp, rtcpFb) находим
             * соответствующий ему конфигурационный объект в текущей sdp.
             * Если матчера на конкретный тип нет, то считаем, что матчится любой конф. объект соответствующего типа
             * Ищем через filter, чтобы убедиться, что нашли только 1 объект.
             **/
            const rtpConfigItems = mediaSection.rtp.filter(payloadTypeOption.match.rtp);
            const fmtpConfigItems =
                payloadTypeOption.match.fmtp && mediaSection.fmtp.filter(payloadTypeOption.match.fmtp);
            const rtcpFbConfigItems =
                payloadTypeOption.match.rtcpFb && mediaSection.rtcpFb?.filter(payloadTypeOption.match.rtcpFb);

            /**
             * Если хоть где-то нашли > 1 элемента, значит матчер невалидный.
             */
            if (
                rtpConfigItems.length !== 1 ||
                (fmtpConfigItems && fmtpConfigItems.length !== 1) ||
                (rtcpFbConfigItems && rtcpFbConfigItems.length !== 1)
            ) {
                logger.warn('can not apply payload type option. found !== 1 items. matcher:', {
                    method: 'applyPayloadTypes',
                    additional: {
                        payload: payloadTypeOption.payload,
                    },
                });
                return;
            }

            const configItemPayloadTypes = [
                rtpConfigItems[0].payload,
                fmtpConfigItems?.[0]?.payload,
                rtcpFbConfigItems?.[0]?.payload,
            ].filter(Boolean);
            const oldPayloadType = configItemPayloadTypes[0];

            if (oldPayloadType === undefined) {
                logger.warn('can not apply payload type option. can not determine old payload type. matcher:', {
                    method: 'applyPayloadTypes',
                    additional: {
                        payload: payloadTypeOption.payload,
                    },
                });
                return;
            }

            const isSamePayloadType = configItemPayloadTypes.every(
                (payloadType) => payloadType && payloadType === oldPayloadType
            );

            /**
             * Все найденные объекты должны иметь одинаковый payload type. Иначе мы нашли что-то не то.
             */
            if (!isSamePayloadType) {
                logger.warn('can not apply payload type option. found payload types must be the same. matcher:', {
                    method: 'applyPayloadTypes',
                    additional: {
                        payload: payloadTypeOption.payload,
                    },
                });
                return;
            }

            // Находим все конфигурационные объекты со старым payload type в SDP
            const hasOldPayloadType = (item: MediaConfigItem) => item.payload === oldPayloadType;
            const rtpConfigItem = mediaSection.rtp.find(hasOldPayloadType);
            const fmtpConfigItem = mediaSection.fmtp.find(hasOldPayloadType);
            const rtcpFbConfigItem = mediaSection.rtcpFb?.find(hasOldPayloadType);

            // Обновляем payload type во всех конфигурационных объектах
            if (rtpConfigItem) {
                rtpConfigItem.payload = newPayloadType;
            }
            if (fmtpConfigItem) {
                fmtpConfigItem.payload = newPayloadType;
            }
            if (rtcpFbConfigItem) {
                rtcpFbConfigItem.payload = newPayloadType;
            }

            // Обновляем payload type в свойстве payloads медиа секции
            patchMediaSectionPayloads(mediaSection, oldPayloadType, newPayloadType);
        });
    });
}
